/*
 *
 * Copyright (c) 2020 TenderHub LLC. All rights reserved
 *
 * This software is the confidential and proprietary information of
 * TenderHub LLC ("Confidential Information"). You shall not disclose
 * such Confidential Information.
 *
 */

import { ClipboardModule } from '@angular/cdk/clipboard';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { EntityDataModule } from '@ngrx/data';
import {
	BreakpointModule,
	ConfigModel,
	ConfigModule,
	ConfigStatic,
	ENDPOINT_FILTERS,
	LocaleModule,
	TranslocoRootModule,
} from '@site-shared/common';
import { CoreLibModule } from '@site-shared/core';
import { CurtainModule, CurtainService, ModalRootModule } from '@site-shared/curtain';
import {
	ActionModule,
	AgentRegisterProfile,
	AssociationModule,
	HelpProfile,
	ItemProfile,
	ItemRootModule,
	LookupModule,
	MdmProfile,
	MdmUnitModule,
	PragmaProfile,
	QueryModule,
	SearchContainerModule,
	SuggestionRootModule,
	UserInviteProfile,
} from '@site-shared/data';
import { ExceptionLevel, ExceptionModule, IExceptionOptions } from '@site-shared/exception';
import { FormsRootModule } from '@site-shared/forms';
import { ReflectiveHttpModule } from '@site-shared/http';
import { MdmModule, MdmRootModule } from '@site-shared/mdm';
import { MfeModule } from '@site-shared/mfe';
import { PublicModule } from '@site-shared/public';
import { IS_AUTHORIZED } from '@site-shared/session';
import { ShellIconModule } from '@site-shared/shell';
import { SuggestionPublicService } from '@site-shared/suggestion';
import { NgrxFormsModule } from 'ngrx-forms';
import { LocalStorageService, NgxWebstorageModule } from 'ngx-webstorage';
import { of } from 'rxjs';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { configureCore } from './configure-core';

const SIGNON_EXCEPTION_OPTIONS: IExceptionOptions = {
	[-1]: {
		level: ExceptionLevel.REDIRECT,
	},
	0: {
		level: ExceptionLevel.REDIRECT,
	},
	200: {
		level: ExceptionLevel.REDIRECT,
	},
	400: {
		level: ExceptionLevel.REDIRECT,
	},
	401: {
		level: ExceptionLevel.REDIRECT,
	},
	403: {
		level: ExceptionLevel.REDIRECT,
	},
	404: {
		level: ExceptionLevel.REDIRECT,
	},
	500: {
		level: ExceptionLevel.REDIRECT,
	},
};

@NgModule({
	declarations: [AppComponent],
	imports: [
		PublicModule,
		BrowserModule,
		HttpClientModule,
		AppRoutingModule,
		MdmUnitModule.forRoot(),
		MdmRootModule,
		MdmModule.forFeature(),
		ActionModule.forRoot(),
		QueryModule.forRoot(),
		AssociationModule.forRoot(),
		LookupModule.forRoot(),
		ItemRootModule.forRoot(),
		EntityDataModule.forRoot({}),
		NgrxFormsModule,
		ConfigModule.forRoot({
			configUrl: environment.configUrl,
			configConstructable: ConfigModel,
		}),
		TranslocoRootModule.forRoot(),
		LocaleModule.forRoot({
			storeToken: 'locale',
			localeConfig: environment.localeConfig,
		}),
		CoreLibModule.forRoot(
			{
				localeId: environment.locale,
				logoutUrl: environment.logoutUrl,
				apiUrl: environment.apiUrl,
				isProduction: environment.production,
				authorizationMethod: environment.authorizationMethod,
				authorizationKey: environment.authorizationKey,
				accessTokenKey: environment.accessTokenKey,
				loginUrl: environment.loginUrl,
				profiles: [
					AgentRegisterProfile,
					HelpProfile,
					UserInviteProfile,
					MdmProfile,
					ItemProfile,
					PragmaProfile,
				],
				storage: LocalStorageService,
				disclaimersUrl: environment.disclaimersUrl,
				recoveryTokenKey: environment.recoveryTokenKey,
				rootTitle: environment.rootTitle,
				application: environment.application,
			},
			configureCore
		),
		NgxWebstorageModule.forRoot({
			prefix: 'sf.signon',
			separator: '.',
			caseSensitive: true,
		}),
		CurtainModule.forRoot(),
		ClipboardModule,
		ModalRootModule.forRoot(),
		ReflectiveHttpModule.forRoot(environment.endpoints),
		BreakpointModule.forRoot(),
		ShellIconModule,
		MfeModule.forFeature({
			curtainOpener: CurtainService,
		}),
		FormsRootModule,
		SearchContainerModule.forRoot({
			Suggestion: SuggestionPublicService,
		}),
		ExceptionModule.forRoot(SIGNON_EXCEPTION_OPTIONS),
		SuggestionRootModule.forRoot(),
	],
	bootstrap: [AppComponent],
	providers: [
		{
			provide: IS_AUTHORIZED,
			useValue: of(false),
		},
		{
			provide: ENDPOINT_FILTERS,
			useValue: [],
		},
	],
})
export class AppModule {
	constructor(config: ConfigStatic<any>) {
		config.load();
	}
}
